import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";

import { useNavigate } from "react-router-dom";
import { Box, Tabs, Tab } from "@mui/material";
import {
  findGetParameter,
  newPath,
} from "../../components/utilities/UtilityFunctions";

import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";

import AdminEventUsers from "./components/AdminEventUsers";
import AdminOrganizations from "./components/AdminOrganizations";
import AdminEventRoles from "./components/AdminEventRoles";
import AdminSubFactions from "./components/AdminSubFactions";
import AdminArmyUnits from "./components/AdminArmyUnits";
import AdminExports from "./components/AdminExports";
import AdminGameSystems from "./components/AdminGameSystems";
import AdminArmies from "./components/AdminArmies";
import { getEnvironmentVariable } from "../../global/environmentVariables";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  );
}

function a11yProps(index) {
  return {
    id: `admin-tab-${index}`,
    "aria-controls": `admin-tabpanel-${index}`,
  };
}

var tabIndexes = {
  organizations: 0,
  "event-users": 1,
  "event-roles": 2,
  "game-systems": 3,
};
var tabIndex = 3;
if (getEnvironmentVariable("SUPPORTS_ARMIES") === "true") {
  tabIndex++;
  tabIndexes["armies"] = tabIndex;
}
if (getEnvironmentVariable("SUPPORTS_SUB_FACTIONS") === "true") {
  tabIndex++;
  tabIndexes["sub-factions"] = tabIndex;
}
if (getEnvironmentVariable("SUPPORTS_ARMY_UNITS") === "true") {
  tabIndex++;
  tabIndexes["army-units"] = tabIndex;
}
tabIndex++;
tabIndexes.exports = tabIndex;

export default function Admin() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const activeTabParam = findGetParameter("active_tab");

  const [activeTab, setActiveTab] = useState(
    activeTabParam
      ? tabIndexes[activeTabParam.toLowerCase()] !== undefined
        ? tabIndexes[activeTabParam.toLowerCase()]
        : 0
      : 0
  );

  const handleTabChange = async (e, newTab, freshEvent) => {
    setLoading(true);
    try {
      await hasAdminAccess(authContext, newTab, true);
    } catch (err) {
      setLoading(false);
      return;
    }

    var path;
    Object.keys(tabIndexes).forEach((tabIndex) => {
      if (tabIndexes[tabIndex] === newTab) {
        path = newPath({
          active_tab: tabIndex,
        });

        return true;
      }
    });
    setActiveTab(newTab);
    navigate(path, { replace: false });
    setLoading(false);
  };

  useEffect(() => {
    const go = async () => {
      try {
        await hasAdminAccess(authContext, activeTab, true);
      } catch (err) {
        navigate(
          authContext?.authState?.identity?.adminGameSystems?.length
            ? "/admin"
            : "/home"
        );
        setLoading(false);

        return;
      }

      setLoading(false);
    };
    go();
    // eslint-disable-next-line
  }, [activeTab]);

  return (
    <>
      {Boolean(loading) && <ModalLoadingFull />}
      <Box sx={{ width: "100%" }}>
        <TabPanel value={activeTab} index={tabIndexes["organizations"]}>
          <AdminOrganizations
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={tabIndexes["event-users"]}>
          <AdminEventUsers
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={tabIndexes["event-roles"]}>
          <AdminEventRoles
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </TabPanel>

        {tabIndexes["armies"] !== undefined && (
          <TabPanel value={activeTab} index={tabIndexes["armies"]}>
            <AdminArmies
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              handleTabChange={handleTabChange}
              authContext={authContext}
            />
          </TabPanel>
        )}

        {tabIndexes["sub-factions"] !== undefined && (
          <TabPanel value={activeTab} index={tabIndexes["sub-factions"]}>
            <AdminSubFactions
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              handleTabChange={handleTabChange}
              authContext={authContext}
            />
          </TabPanel>
        )}

        {tabIndexes["army-units"] !== undefined && (
          <TabPanel value={activeTab} index={tabIndexes["army-units"]}>
            <AdminArmyUnits
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              handleTabChange={handleTabChange}
              authContext={authContext}
            />
          </TabPanel>
        )}

        <TabPanel value={activeTab} index={tabIndexes["exports"]}>
          <AdminExports
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={tabIndexes["game-systems"]}>
          <AdminGameSystems
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </TabPanel>
      </Box>
    </>
  );
}

export function AdminTabs(props) {
  const { activeTab, handleTabChange } = props;
  const authContext = useContext(AuthContext);

  return (
    <Box
      sx={{
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          maxWidth: "100%",
          backgroundColor: "banner.main",
          color: "banner.contrastText",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="admin tabs"
        >
          <Tab
            label="Organizations"
            disabled={!authContext?.authState?.identity?.isAdmin}
            {...a11yProps(tabIndexes["organizations"])}
          />
          <Tab
            label="Event Users"
            disabled={!authContext?.authState?.identity?.isAdmin}
            {...a11yProps(tabIndexes["event-users"])}
          />
          <Tab
            label="Event Roles"
            disabled={!authContext?.authState?.identity?.isAdmin}
            {...a11yProps(tabIndexes["event-roles"])}
          />
          <Tab
            label="Game Systems"
            {...a11yProps(tabIndexes["game-systems"])}
          />
          {tabIndexes["armies"] !== undefined && (
            <Tab label="Armies" {...a11yProps(tabIndexes["armies"])} />
          )}
          {tabIndexes["sub-factions"] !== undefined && (
            <Tab
              label="Sub Factions"
              {...a11yProps(tabIndexes["sub-factions"])}
            />
          )}
          {tabIndexes["army-units"] !== undefined && (
            <Tab label="Army Units" {...a11yProps(tabIndexes["army-units"])} />
          )}
          <Tab
            label="Exports"
            disabled={!authContext?.authState?.identity?.isAdmin}
            {...a11yProps(tabIndexes["exports"])}
          />
        </Tabs>
      </Box>
    </Box>
  );
}

const gameSystemAdminTabs = [
  "game-systems",
  "armies",
  "sub-factions",
  "army-units",
];

export async function hasAdminAccess(authContext, tabIndex, notify) {
  var err;

  if (authContext?.authState?.identity?.isAdmin === true) {
    return true;
  }
  if (authContext?.authState?.identity?.adminGameSystems?.length > 0) {
    for (let gameSystemAdminTab of gameSystemAdminTabs) {
      if (tabIndexes[gameSystemAdminTab] === tabIndex) {
        return true;
      }
    }
  }

  err = new Error("You do not have access to this function");
  if (notify) postNotification("warning", err);

  if (err) {
    throw err;
  }
  return false;
}
